import { useEffect, useMemo, useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'

import $api from 'http'
import Notification from 'utils/Notifications'
import {
  PageHeader,
  Skeleton,
  EmptyState,
  ErrorState,
  Card,
} from 'components'

import JournalToolbar from './components/JournalToolbar/JournalToolbar'
import JournalGrid from './components/JournalGrid/JournalGrid'
import styles from './JournalPage.module.scss'

const SHORTCUTS = {
  '1': 'present',
  '2': 'absent',
  '3': 'late',
  '4': 'excused',
}

const mapDraftMarks = (draft) => {
  const out = {}
  Object.entries(draft).forEach(([k, v]) => {
    if (k.startsWith('mark|')) {
      const rest = k.slice('mark|'.length)
      out[rest] = v
    }
  })
  return out
}

const JournalPage = () => {
  const { t } = useTranslation('common')
  const [params] = useSearchParams()
  const lessonId = params.get('lesson')
  const groupParam = params.get('group')

  const [lesson, setLesson] = useState(null)
  const [students, setStudents] = useState([])
  const [lessons, setLessons] = useState([])
  const [attendance, setAttendance] = useState({})
  const [marks, setMarks] = useState({})
  const [draft, setDraft] = useState({})
  const [activeCell, setActiveCell] = useState(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [saving, setSaving] = useState(false)
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const STATUS_LIST = useMemo(
    () => [
      { key: 'present', label: t('journal.status_present_short') },
      { key: 'absent', label: t('journal.status_absent_short') },
      { key: 'late', label: t('journal.status_late_short') },
      { key: 'excused', label: t('journal.status_excused_short') },
    ],
    [t]
  )

  const loadLessonAndGroup = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      let groupId = groupParam
      let curLesson = null
      if (lessonId) {
        try {
          const r = await $api.get(`/academic/lessons/${lessonId}/`)
          curLesson = r.data
          groupId = curLesson.group?.id || curLesson.group || groupId
          setLesson(curLesson)
        } catch {

        }
      }

      if (groupId) {
        try {
          const r = await $api.get('/students/', { params: { group: groupId } })
          setStudents(r.data?.results || r.data || [])
        } catch {
          setStudents([])
        }
      }

      if (groupId) {
        try {
          const today = new Date()
          const from = new Date(today)
          from.setDate(from.getDate() - 30)
          const r = await $api.get('/academic/lessons/', {
            params: {
              group: groupId,
              date_from: from.toISOString().slice(0, 10),
              date_to: today.toISOString().slice(0, 10),
            },
          })
          const list = r.data?.results || r.data || []
          setLessons(list.sort((a, b) => (a.start_at || '').localeCompare(b.start_at || '')))
        } catch {
          setLessons([])
        }
      } else if (curLesson) {
        setLessons([curLesson])
      }
    } catch {
      setLoadError(true)
      setNotify({ isOpen: true, message: t('journal.load_failed'), type: 'error', sound: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const loadAttendanceAndMarks = async () => {
    if (!lessons.length) return
    const lessonIds = lessons.map((l) => l.id)
    try {
      const aMap = {}
      const mMap = {}
      await Promise.all(
        lessonIds.map(async (lid) => {
          try {
            const ar = await $api.get('/academic/attendance/', { params: { lesson: lid } })
            const items = ar.data?.results || ar.data || []
            items.forEach((it) => {
              aMap[`${lid}|${it.student?.id || it.student}`] = it.status
            })
          } catch {

          }
          try {
            const mr = await $api.get('/academic/marks/', { params: { lesson: lid } })
            const items = mr.data?.results || mr.data || []
            items.forEach((it) => {
              mMap[`${lid}|${it.student?.id || it.student}`] = it.value ?? it.score ?? ''
            })
          } catch {

          }
        })
      )
      setAttendance(aMap)
      setMarks(mMap)
      setDraft({})
    } catch {

    }
  }

  useEffect(() => {
    loadLessonAndGroup()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId, groupParam])

  useEffect(() => {
    loadAttendanceAndMarks()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessons])

  const handleKey = useCallback(
    (e) => {
      if (!activeCell) return
      const status = SHORTCUTS[e.key]
      if (status) {
        e.preventDefault()
        const key = `${activeCell.lessonId}|${activeCell.studentId}`
        setDraft((d) => ({ ...d, [`att|${key}`]: status }))
      }
    },
    [activeCell]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [handleKey])

  const getAtt = (lid, sid) => draft[`att|${lid}|${sid}`] ?? attendance[`${lid}|${sid}`] ?? ''
  const getMark = (lid, sid) => draft[`mark|${lid}|${sid}`] ?? marks[`${lid}|${sid}`] ?? ''

  const setAtt = (lid, sid, val) => {
    setDraft((d) => ({ ...d, [`att|${lid}|${sid}`]: val }))
  }
  const setMarkVal = (lid, sid, val) => {
    setDraft((d) => ({ ...d, [`mark|${lid}|${sid}`]: val }))
  }

  const averageScore = (sid) => {
    const vals = []
    Object.entries({ ...marks, ...mapDraftMarks(draft) }).forEach(([k, v]) => {
      const [, studentId] = k.split('|')
      if (String(studentId) === String(sid) && v !== '' && v != null) {
        const n = Number(v)
        if (!isNaN(n)) vals.push(n)
      }
    })
    if (!vals.length) return '—'
    const avg = vals.reduce((a, b) => a + b, 0) / vals.length
    return avg.toFixed(2)
  }

  const save = async () => {
    setSaving(true)
    try {
      const byLesson = {}
      Object.entries(draft).forEach(([k, v]) => {
        if (k.startsWith('att|')) {
          const [, lid, sid] = k.split('|')
          if (!byLesson[lid]) byLesson[lid] = []
          byLesson[lid].push({ student: Number(sid), status: v })
        }
      })
      await Promise.all(
        Object.entries(byLesson).map(([lid, items]) =>
          $api.post('/academic/attendance/bulk/', { lesson: Number(lid), items })
        )
      )

      const markEntries = Object.entries(draft).filter(([k]) => k.startsWith('mark|'))
      await Promise.all(
        markEntries.map(([k, v]) => {
          const [, lid, sid] = k.split('|')
          if (v === '' || v == null) return null
          return $api.post('/academic/marks/', {
            lesson: Number(lid),
            student: Number(sid),
            value: v,
            kind: 'current',
          })
        })
      )

      setNotify({ isOpen: true, message: t('journal.saved'), type: 'success', sound: 'success' })
      loadAttendanceAndMarks()
    } catch {
      setNotify({ isOpen: true, message: t('journal.save_failed'), type: 'error', sound: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const dirty = useMemo(() => Object.keys(draft).length > 0, [draft])

  if (loading) {
    return (
      <div className={styles.page}>
        <Skeleton variant="card" />
        <Skeleton variant="table" rows={10} cols={6} />
      </div>
    )
  }

  if (loadError) {
    return (
      <div className={styles.page}>
        <ErrorState title={t('journal.load_failed')} onRetry={loadLessonAndGroup} />
        <Notification notify={notify} setNotify={setNotify} />
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('journal.title', 'Журнал')}
        subtitle={lesson?.subject_name || lesson?.subject?.name || ''}
      />

      <JournalToolbar
        lesson={lesson}
        dirty={dirty}
        saving={saving}
        onSave={save}
        onReset={() => setDraft({})}
      />

      {students.length === 0 ? (
        <Card>
          <EmptyState iconName="users" title={t('journal.no_students_in_group')} />
        </Card>
      ) : (
        <JournalGrid
          students={students}
          lessons={lessons}
          statusList={STATUS_LIST}
          activeCell={activeCell}
          setActiveCell={setActiveCell}
          getAtt={getAtt}
          getMark={getMark}
          setAtt={setAtt}
          setMarkVal={setMarkVal}
          averageScore={averageScore}
        />
      )}

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default JournalPage
